/**
 * SmartImprove Publish Band — Phase 6.
 *
 * Mirrors the SmartEvaluate publish band visual + behavioral language and
 * adds the SmartImprove-specific versioning lifecycle:
 *
 *   Draft → Ready for Review → Finalized → Published
 *
 * Actions surfaced (consultant / admin):
 *   - Finalize Version
 *   - Create New Version (clones current; unlocks for edits)
 *   - Publish to Client (immutable snapshot + initiative activation)
 *   - Unpublish (soft — clears visibility, preserves snapshot)
 *   - View Version History (drawer)
 *
 * Once finalized:
 *   - Tabs' Accept/Reject/Edit/Regenerate are server-locked (409 returned)
 *     — we also disable them visually by passing `isFinalized` down.
 *   - Only "Create New Version" + "Publish" remain available.
 *
 * The band lives BETWEEN the engagement header and the Tabs navigation.
 */
import { useEffect, useState, useCallback } from "react";
import {
  CheckCircle2,
  Send,
  History,
  GitBranch,
  ShieldCheck,
  XCircle,
  Lock,
  Loader2,
  Mail,
  Download,
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { Button } from "../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import VersionHistoryDrawer from "./VersionHistoryDrawer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/smartimprove`;
const ax = axios.create({ baseURL: API, withCredentials: true });

export default function PublishBand({ engagement, onChange }) {
  const engagementId = engagement?.id;
  const isFinalized = !!engagement?.is_finalized;
  const versionNumber = engagement?.finalized_version_number;

  const [release, setRelease] = useState(null);
  const [busy, setBusy] = useState(null); // 'finalize' | 'clone' | 'publish' | 'unpublish'
  const [historyOpen, setHistoryOpen] = useState(false);
  const [publishOpen, setPublishOpen] = useState(false);
  const [confirmFinalize, setConfirmFinalize] = useState(false);
  const [confirmClone, setConfirmClone] = useState(false);
  const [confirmUnpublish, setConfirmUnpublish] = useState(false);
  const [showEmailConfirm, setShowEmailConfirm] = useState(false);
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [emailTo, setEmailTo] = useState("");
  const [emailMessage, setEmailMessage] = useState("");
  const [emailSending, setEmailSending] = useState(false);

  const reloadRelease = useCallback(async () => {
    if (!engagementId) return;
    try {
      const r = await ax.get(`/engagements/${engagementId}/release-status`);
      setRelease(r.data || null);
    } catch {
      setRelease(null);
    }
  }, [engagementId]);

  useEffect(() => {
    reloadRelease();
  }, [reloadRelease]);

  const currentReleasedVersionId = release?.source_version?.version_id || null;
  const currentVersionId = engagement?.finalized_version_id || null;
  const currentVersionPublished =
    !!release?.released_to_client &&
    !!currentVersionId &&
    currentReleasedVersionId === currentVersionId;

  const doFinalize = async () => {
    setBusy("finalize");
    try {
      const r = await ax.post(`/engagements/${engagementId}/finalize`);
      if (r.data?.status === "already_finalized") {
        toast.message("Already finalized");
      } else {
        toast.success(`Version v${r.data?.version?.version_number} finalized`);
      }
      setConfirmFinalize(false);
      onChange && (await onChange());
      await reloadRelease();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to finalize");
    } finally {
      setBusy(null);
    }
  };

  const doClone = async () => {
    setBusy("clone");
    try {
      const r = await ax.post(`/engagements/${engagementId}/versions/current/clone`);
      toast.success(`New draft v${r.data?.draft?.version_number} unlocked`);
      setConfirmClone(false);
      onChange && (await onChange());
      await reloadRelease();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create new version");
    } finally {
      setBusy(null);
    }
  };

  const doUnpublish = async () => {
    setBusy("unpublish");
    try {
      await ax.post(`/engagements/${engagementId}/unpublish`);
      toast.success("Unpublished from client portal");
      setConfirmUnpublish(false);
      await reloadRelease();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to unpublish");
    } finally {
      setBusy(null);
    }
  };

  return (
    <section
      data-testid="smartimprove-publish-band"
      data-finalized={isFinalized ? "true" : "false"}
      data-published={currentVersionPublished ? "true" : "false"}
      className="bg-white border border-[#E5E7EB] rounded-sm px-4 py-3"
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <LifecycleBadge
            isFinalized={isFinalized}
            isPublished={currentVersionPublished}
          />
          {isFinalized && versionNumber ? (
            <span
              className="inline-flex items-center px-1.5 py-0.5 text-[10px] font-semibold border rounded-sm bg-[#F8FAFC] text-[#374151] border-[#E5E7EB]"
              data-testid="publish-band-version-pill"
            >
              v{versionNumber}
            </span>
          ) : null}
          {release?.released_to_client ? (
            <PublishMeta release={release} />
          ) : (
            <span className="text-xs text-[#6B7280]">
              {isFinalized
                ? "Finalized — ready to publish to client portal."
                : "Draft in progress. Finalize when ready for client review."}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            size="sm"
            variant="outline"
            onClick={() => setHistoryOpen(true)}
            className="text-[11px] h-7 px-2 border-[#E5E7EB]"
            data-testid="publish-band-history-btn"
          >
            <History className="w-3 h-3 mr-1" />
            Version History
          </Button>

          {!isFinalized ? (
            <Button
              size="sm"
              onClick={() => setConfirmFinalize(true)}
              disabled={busy === "finalize"}
              className="text-[11px] h-7 px-3 bg-[#025979] hover:bg-[#024a63] text-white"
              data-testid="publish-band-finalize-btn"
            >
              {busy === "finalize" ? (
                <Loader2 className="w-3 h-3 mr-1 animate-spin" />
              ) : (
                <ShieldCheck className="w-3 h-3 mr-1" />
              )}
              Finalize Version
            </Button>
          ) : (
            <>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setConfirmClone(true)}
                disabled={busy === "clone"}
                className="text-[11px] h-7 px-3 border-[#025979] text-[#025979] hover:bg-[#F8FAFC]"
                data-testid="publish-band-new-version-btn"
              >
                {busy === "clone" ? (
                  <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                ) : (
                  <GitBranch className="w-3 h-3 mr-1" />
                )}
                Create New Version
              </Button>
              {currentVersionPublished ? (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setConfirmUnpublish(true)}
                  disabled={busy === "unpublish"}
                  className="text-[11px] h-7 px-3 border-[#FECACA] text-[#B91C1C] hover:bg-[#FEF2F2]"
                  data-testid="publish-band-unpublish-btn"
                >
                  {busy === "unpublish" ? (
                    <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                  ) : (
                    <XCircle className="w-3 h-3 mr-1" />
                  )}
                  Unpublish
                </Button>
              ) : (
                <Button
                  size="sm"
                  onClick={() => setPublishOpen(true)}
                  disabled={!isFinalized}
                  className="text-[11px] h-7 px-3 bg-[#025979] hover:bg-[#024a63] text-white"
                  data-testid="publish-band-publish-btn"
                >
                  <Send className="w-3 h-3 mr-1" />
                  Publish to Client Portal
                </Button>
              )}
            </>
          )}
        </div>
      </div>

      {isFinalized && !currentVersionPublished && release?.unpublished_at ? (
        <p
          className="text-[10px] text-[#B91C1C] mt-2"
          data-testid="publish-band-unpublished-note"
        >
          Previously unpublished on{" "}
          {new Date(release.unpublished_at).toLocaleString()} — republish to
          restore client access.
        </p>
      ) : null}

      {/* Row 2 — Executive output actions (finalized only). Matches
          SmartEvaluate's Send PDF / Download PDF placement. Wired to the
          SmartImprove reportlab PDF endpoints. */}
      {isFinalized && engagement?.finalized_version_id ? (
        <div
          className="mt-2 pt-2 border-t border-dashed border-[#E5E7EB]"
          data-testid="publish-band-executive-actions"
        >
          <div className="flex flex-wrap items-center justify-end gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => setShowEmailConfirm(true)}
              className="text-[11px] h-7 px-3 border-[#025979] text-[#025979] hover:bg-[#E0F2FE]"
              data-testid="publish-band-send-pdf-btn"
              title="Send the finalized Executive Summary PDF by email"
            >
              <Mail className="w-3 h-3 mr-1" /> Send Final PDF by Email
            </Button>
            <a
              href={`${API}/engagements/${engagement.id}/versions/${engagement.finalized_version_id}/report.pdf`}
              download
              className="inline-flex items-center gap-1 px-3 h-7 text-[11px] font-semibold bg-[#025979] hover:bg-[#024a63] text-white rounded-md transition-colors"
              data-testid="publish-band-download-pdf-btn"
              title="Download the finalized Executive Summary PDF"
            >
              <Download className="w-3 h-3" /> Download Final PDF
            </a>
          </div>
          <p
            className="mt-1 text-[10px] text-[#9CA3AF] text-right"
            data-testid="publish-band-pdf-tip"
          >
            Tip: right-click the Download button and choose “Save link as…” to download.
          </p>
        </div>
      ) : null}

      <FinalizeDialog
        open={confirmFinalize}
        onOpenChange={setConfirmFinalize}
        onConfirm={doFinalize}
        busy={busy === "finalize"}
      />
      <CloneDialog
        open={confirmClone}
        onOpenChange={setConfirmClone}
        onConfirm={doClone}
        currentVersionNumber={versionNumber}
        busy={busy === "clone"}
      />
      <UnpublishDialog
        open={confirmUnpublish}
        onOpenChange={setConfirmUnpublish}
        onConfirm={doUnpublish}
        busy={busy === "unpublish"}
      />
      <PublishDialog
        open={publishOpen}
        onOpenChange={setPublishOpen}
        engagement={engagement}
        defaultEmails={release?.released_for_emails || []}
        onPublished={async () => {
          setPublishOpen(false);
          await reloadRelease();
          onChange && (await onChange());
        }}
      />
      <VersionHistoryDrawer
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        engagementId={engagementId}
        onChanged={async () => {
          await reloadRelease();
          onChange && (await onChange());
        }}
      />
      <EmailConfirmDialog
        open={showEmailConfirm}
        onOpenChange={setShowEmailConfirm}
        onConfirm={() => {
          setShowEmailConfirm(false);
          setShowEmailDialog(true);
        }}
      />
      <EmailPdfDialog
        open={showEmailDialog}
        onOpenChange={setShowEmailDialog}
        engagement={engagement}
        to={emailTo}
        setTo={setEmailTo}
        message={emailMessage}
        setMessage={setEmailMessage}
        sending={emailSending}
        setSending={setEmailSending}
      />
    </section>
  );
}

function LifecycleBadge({ isFinalized, isPublished }) {
  if (isPublished) {
    return (
      <span
        className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-semibold border rounded-sm bg-[#DCFCE7] text-[#166534] border-[#BBF7D0]"
        data-testid="lifecycle-badge-published"
      >
        <CheckCircle2 className="w-3 h-3" />
        Published
      </span>
    );
  }
  if (isFinalized) {
    return (
      <span
        className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-semibold border rounded-sm bg-[#FEF3C7] text-[#92400E] border-[#FDE68A]"
        data-testid="lifecycle-badge-finalized"
      >
        <Lock className="w-3 h-3" />
        Finalized
      </span>
    );
  }
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-semibold border rounded-sm bg-[#F1F5F9] text-[#475569] border-[#E2E8F0]"
      data-testid="lifecycle-badge-draft"
    >
      Draft
    </span>
  );
}

function PublishMeta({ release }) {
  const emails = release.released_for_emails || [];
  return (
    <div className="text-[11px] text-[#0369A1] min-w-0" data-testid="publish-band-meta">
      <div>
        Published on{" "}
        <span className="font-semibold">
          {release.released_at ? new Date(release.released_at).toLocaleString() : "—"}
        </span>
        {release.released_by?.email ? (
          <span> · by {release.released_by.email}</span>
        ) : null}
        {release.source_version?.version_number ? (
          <span> · v{release.source_version.version_number}</span>
        ) : null}
      </div>
      {emails.length > 0 ? (
        <div className="mt-1 flex flex-wrap gap-1">
          <span className="text-[#475569]">Access:</span>
          {emails.map((em) => (
            <span
              key={em}
              data-testid={`publish-band-email-${em}`}
              className="inline-flex items-center px-1.5 py-0.5 text-[10px] font-medium bg-[#E0F2FE] border border-[#025979]/20 text-[#025979] rounded-sm"
            >
              {em}
            </span>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function EmailConfirmDialog({ open, onOpenChange, onConfirm }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-sm"
        data-testid="publish-band-email-confirm-dialog"
      >
        <DialogHeader>
          <DialogTitle
            className="text-sm font-semibold text-[#111827]"
            style={{ fontFamily: "Chivo" }}
          >
            Send SmartImprove report via email?
          </DialogTitle>
          <DialogDescription className="text-[11px] text-[#6B7280] leading-relaxed">
            The finalized SmartImprove Executive Report will be shared as a PDF attachment.<br />
            You can review the recipient details before sending.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="flex items-center justify-end gap-2">
          <Button
            variant="outline"
            size="sm"
            className="text-xs h-8 px-3 border-[#E5E7EB]"
            onClick={() => onOpenChange(false)}
            data-testid="publish-band-email-confirm-no-btn"
          >
            No, go back
          </Button>
          <Button
            size="sm"
            className="text-xs h-8 px-3 bg-[#025979] hover:bg-[#024a63] text-white"
            onClick={onConfirm}
            data-testid="publish-band-email-confirm-yes-btn"
          >
            <Mail className="w-3 h-3 mr-1.5" /> Yes, compose
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EmailPdfDialog({ open, onOpenChange, engagement, to, setTo, message, setMessage, sending, setSending }) {
  const send = async () => {
    const target = (to || "").trim();
    if (!target) {
      toast.error("Recipient email is required");
      return;
    }
    setSending(true);
    try {
      await ax.post(
        `/engagements/${engagement.id}/versions/${engagement.finalized_version_id}/email-report`,
        { to: target, message: message || undefined },
      );
      toast.success(`Final PDF sent to ${target}`);
      onOpenChange(false);
      setMessage("");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed to send PDF");
    } finally {
      setSending(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="publish-band-email-dialog">
        <DialogHeader>
          <DialogTitle>Send Final PDF by Email</DialogTitle>
          <DialogDescription>
            Sends the finalized SmartImprove Executive PDF as an attachment.
            Uses the platform email template (`smartimprove_report_share`).
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <label className="text-[10px] uppercase font-semibold text-[#6B7280] tracking-wider mb-1 block">
              Recipient
            </label>
            <Input
              type="email"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              placeholder="cfo@client.com"
              className="rounded-sm text-sm"
              data-testid="publish-band-email-to"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase font-semibold text-[#6B7280] tracking-wider mb-1 block">
              Optional message (prepended to email body)
            </label>
            <Textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={3}
              className="rounded-sm text-sm"
              placeholder="Sharing the finalized SmartImprove business case for your review…"
              data-testid="publish-band-email-message"
            />
          </div>
        </div>
        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            className="rounded-sm text-xs"
            data-testid="publish-band-email-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={send}
            disabled={sending}
            className="bg-[#025979] hover:bg-[#024a63] text-white rounded-sm text-xs"
            data-testid="publish-band-email-send"
          >
            {sending ? "Sending…" : "Send"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FinalizeDialog({ open, onOpenChange, onConfirm, busy }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="finalize-confirm-dialog">
        <DialogHeader>
          <DialogTitle>Finalize this version?</DialogTitle>
          <DialogDescription>
            Snapshots all accepted insights, recommendations, ROI, implementation,
            and executive summary into an immutable version. Drafts &amp; rejected
            rows are excluded. After finalize the workspace becomes read-only —
            you can always Create New Version to edit further.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="text-xs rounded-sm"
          >
            Cancel
          </Button>
          <Button
            onClick={onConfirm}
            disabled={busy}
            className="text-xs bg-[#025979] hover:bg-[#024a63] text-white rounded-sm"
            data-testid="finalize-confirm-btn"
          >
            {busy ? "Finalizing…" : "Finalize Version"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CloneDialog({ open, onOpenChange, onConfirm, currentVersionNumber, busy }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="clone-confirm-dialog">
        <DialogHeader>
          <DialogTitle>Create a new version?</DialogTitle>
          <DialogDescription>
            v{currentVersionNumber} stays finalized (and remains client-visible
            if published). You will continue editing the live state as v
            {(currentVersionNumber || 0) + 1} until you finalize again.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="text-xs rounded-sm"
          >
            Cancel
          </Button>
          <Button
            onClick={onConfirm}
            disabled={busy}
            className="text-xs bg-[#025979] hover:bg-[#024a63] text-white rounded-sm"
            data-testid="clone-confirm-btn"
          >
            {busy ? "Creating…" : "Create New Version"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function UnpublishDialog({ open, onOpenChange, onConfirm, busy }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="unpublish-confirm-dialog">
        <DialogHeader>
          <DialogTitle>Unpublish from client portal?</DialogTitle>
          <DialogDescription>
            Removes client visibility. The published snapshot, version, and
            history are <strong>preserved</strong> — you can republish at any time
            without re-finalizing.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="text-xs rounded-sm"
          >
            Cancel
          </Button>
          <Button
            onClick={onConfirm}
            disabled={busy}
            variant="destructive"
            className="text-xs rounded-sm"
            data-testid="unpublish-confirm-btn"
          >
            {busy ? "Unpublishing…" : "Unpublish"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PublishDialog({ open, onOpenChange, engagement, defaultEmails, onPublished }) {
  const [emails, setEmails] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setEmails((defaultEmails || []).join(", "));
      setMessage("");
    }
  }, [open, defaultEmails]);

  const parseEmails = () =>
    emails
      .split(/[,;\n\s]+/)
      .map((e) => e.trim().toLowerCase())
      .filter((e) => e && e.includes("@"));

  const submit = async () => {
    const parsed = parseEmails();
    if (parsed.length === 0) {
      toast.error("At least one valid email is required");
      return;
    }
    setBusy(true);
    try {
      const r = await ax.post(`/engagements/${engagement.id}/publish`, {
        emails: parsed,
        message: message.trim() || undefined,
      });
      const activated = r.data?.initiatives_activated?.length || 0;
      toast.success(
        `Published to client · ${activated} initiative${activated === 1 ? "" : "s"} activated`
      );
      onPublished && (await onPublished());
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to publish");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="publish-confirm-dialog">
        <DialogHeader>
          <DialogTitle>Publish to Client Portal</DialogTitle>
          <DialogDescription>
            Releases the finalized version to the client. Only listed emails get
            access. Each accepted recommendation becomes a <strong>proposed</strong> initiative
            in the Transformation Program (client/consultant promotes to selected).
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <label className="block text-xs font-semibold text-[#111827] mb-1">
              Portal Access Email IDs <span className="text-red-500">*</span>
            </label>
            <Input
              value={emails}
              onChange={(e) => setEmails(e.target.value)}
              placeholder="alice@acme.com, bob@acme.com"
              className="rounded-sm text-sm"
              data-testid="publish-emails-input"
            />
            <p className="text-[10px] text-[#6B7280] mt-1">
              Separate with commas, semicolons, or spaces.
            </p>
          </div>
          <div>
            <label className="block text-xs font-semibold text-[#111827] mb-1">
              Consultant message (optional)
            </label>
            <Textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={3}
              placeholder="Optional note included in the invite email."
              className="rounded-sm text-sm"
              data-testid="publish-message-input"
            />
          </div>
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="text-xs rounded-sm"
          >
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={busy}
            className="text-xs bg-[#025979] hover:bg-[#024a63] text-white rounded-sm"
            data-testid="publish-confirm-btn"
          >
            <Send className="w-3 h-3 mr-1" />
            {busy ? "Publishing…" : "Publish"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
